import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Flex,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { AxiosError } from "axios";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Status, StepStatus } from "../../enum/common";
import { IMeetingSchedule } from "../../models/Hrm";
import { useRemoveMeetingSchedule } from "../../query/HrmQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";
import { getStatusTag } from "../filter/FilterEnum";
import ModalUpdateMeetingSchedule from "../modal/hrm/ModalUpdateMeetingSchedule";

const { Text } = Typography;

interface WeeklyMeetingGridProps {
  currentWeekStart: Dayjs;
  getListData: (value: Dayjs) => IMeetingSchedule[];
  setSelectedDate?: (date: Dayjs) => void;
  setIsModalOpen?: (isOpen: boolean) => void;
  isHrmView?: boolean;
  onReview?: (status: Status, id: number) => void;
}

const WeeklyMeetingGrid: React.FC<WeeklyMeetingGridProps> = ({
  currentWeekStart,
  getListData,
  setSelectedDate,
  setIsModalOpen,
  isHrmView,
  onReview,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const scrollRef = useRef<HTMLDivElement>(null);
  const removeMutation = useRemoveMeetingSchedule();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<IMeetingSchedule | null>(
    null,
  );

  const handleDelete = async (id: number) => {
    try {
      await removeMutation.mutateAsync(id);
      notify.success("Xóa lịch họp thành công");
    } catch (error: unknown) {
      const msg = error as AxiosError<{ message: string }>;
      notify.error(msg.response?.data?.message || "Xóa lịch họp thất bại");
    }
  };

  const weekDays = Array.from({ length: 7 }).map((_, i) =>
    currentWeekStart.add(i, "day"),
  );

  const dayNames = [
    "Thứ 2",
    "Thứ 3",
    "Thứ 4",
    "Thứ 5",
    "Thứ 6",
    "Thứ 7",
    "Chủ nhật",
  ];

  useEffect(() => {
    if (scrollRef.current) {
      const currentHour = dayjs().hour();
      scrollRef.current.scrollTop = Math.max(0, (currentHour - 2) * 60);
    }
  }, [currentWeekStart]);

  return (
    <div className="w-full overflow-x-auto pb-2">
      <Flex vertical className="min-w-[1000px]">
        <Flex
          className="border-b border-solid border-gray-200"
          style={{ paddingLeft: 60 }}
        >
          {weekDays.map((day, index) => {
            const isToday = day.isSame(dayjs(), "day");
            const dayMeetings = getListData(day);

            return (
              <Flex
                key={`header-${day.format("DD-MM-YYYY")}`}
                flex={1}
                vertical
                align="center"
                className="py-3 border-l border-solid border-gray-100"
              >
                <Text style={{ fontSize: 12 }}>{dayNames[index]}</Text>

                <Flex justify="center" className="mt-1 relative">
                  <Badge
                    count={dayMeetings.length}
                    offset={[0, 5]}
                    size="small"
                  >
                    <Flex
                      align="center"
                      justify="center"
                      className={`rounded-full w-10 h-10 text-lg transition-colors ${
                        isToday
                          ? "bg-blue-600 text-white font-medium shadow-md"
                          : ""
                      }`}
                    >
                      {day.format("DD")}
                    </Flex>
                  </Badge>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
        <Flex
          vertical
          ref={scrollRef}
          style={{ height: 600, overflowY: "auto", position: "relative" }}
        >
          {Array.from({ length: 24 }).map((_, i) => (
            <Flex
              key={`grid-line-${i}`}
              style={{ height: 60, position: "relative" }}
              className="border-b border-solid border-gray-100"
            >
              <Text
                style={{ width: 60, height: 58.9 }}
                className="block text-right pr-3 text-xs text-gray-400 select-none z-10"
              >
                {i === 0 ? "" : `${i}:00`}
              </Text>

              <Flex flex={1}>
                {weekDays.map((day) => (
                  <Flex
                    key={`col-line-${day.format("DD-MM-YYYY")}`}
                    flex={1}
                    className="border-l border-solid border-gray-100"
                  />
                ))}
              </Flex>
            </Flex>
          ))}

          <Flex
            style={{
              position: "absolute",
              top: 0,
              left: 60,
              right: 0,
              bottom: 0,
            }}
          >
            {weekDays.map((day) => {
              const dayMeetings = getListData(day);
              const isToday = day.isSame(dayjs(), "day");

              const sortedMeetings = [...dayMeetings].sort(
                (a, b) =>
                  dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf(),
              );
              const clusters: IMeetingSchedule[][] = [];
              let currentCluster: IMeetingSchedule[] = [];
              let clusterEnd = 0;

              sortedMeetings.forEach((meeting) => {
                const start = dayjs(meeting.startTime).valueOf();
                const end = dayjs(meeting.endTime).valueOf();
                if (currentCluster.length === 0) {
                  currentCluster.push(meeting);
                  clusterEnd = end;
                } else {
                  if (start < clusterEnd) {
                    currentCluster.push(meeting);
                    if (end > clusterEnd) {
                      clusterEnd = end;
                    }
                  } else {
                    clusters.push(currentCluster);
                    currentCluster = [meeting];
                    clusterEnd = end;
                  }
                }
              });
              if (currentCluster.length > 0) {
                clusters.push(currentCluster);
              }

              const eventStyles = new Map<
                number,
                { left: string; width: string }
              >();

              clusters.forEach((cluster) => {
                const columns: IMeetingSchedule[][] = [];
                cluster.forEach((meeting) => {
                  let placed = false;
                  for (let i = 0; i < columns.length; i++) {
                    const col = columns[i];
                    const lastEvent = col[col.length - 1];
                    if (
                      dayjs(meeting.startTime).valueOf() >=
                      dayjs(lastEvent.endTime).valueOf()
                    ) {
                      col.push(meeting);
                      placed = true;
                      break;
                    }
                  }
                  if (!placed) {
                    columns.push([meeting]);
                  }
                });

                const numCols = columns.length;
                columns.forEach((col, colIndex) => {
                  col.forEach((meeting) => {
                    let widthPercent = 100;
                    let leftPercent = 0;

                    if (numCols > 1) {
                      widthPercent = Math.max(100 / numCols, 75);
                      const maxLeft = 100 - widthPercent;
                      leftPercent = (colIndex / (numCols - 1)) * maxLeft;
                    }

                    eventStyles.set(meeting.id, {
                      left: `${leftPercent}%`,
                      width: `${widthPercent}%`,
                    });
                  });
                });
              });

              return (
                <Flex
                  key={`col-event-${day.format("DD-MM-YYYY")}`}
                  flex={1}
                  style={{ position: "relative" }}
                  className="border-l border-transparent"
                >
                  <div
                    className="border-l border-dashed border-blue-200"
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 16,
                      height: 24 * 60,
                      background:
                        "repeating-linear-gradient(to bottom, rgba(59,130,246,0.08), rgba(59,130,246,0.08) 4px, transparent 4px, transparent 8px)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                  {Array.from({ length: 24 }).map((_, i) => (
                    <div
                      key={`click-${i}`}
                      style={{
                        position: "absolute",
                        top: i * 60,
                        height: 60,
                        left: 0,
                        right: 0,
                        zIndex: 1,
                      }}
                      className={
                        isHrmView
                          ? "block"
                          : "cursor-pointer group hover:bg-blue-50 transition-colors block"
                      }
                      title={isHrmView ? undefined : "Bấm để tạo lịch họp"}
                      onClick={() => {
                        if (!isHrmView && setSelectedDate && setIsModalOpen) {
                          setSelectedDate(day.hour(i).minute(0));
                          setIsModalOpen(true);
                        }
                      }}
                    >
                      <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-gray-200 opacity-50 pointer-events-none" />
                      {!isHrmView && (
                        <Flex
                          justify="end"
                          align="center"
                          className="h-full w-full opacity-0 group-hover:opacity-100 transition-opacity pr-1"
                        >
                          <PlusOutlined className="text-blue-500 text-xs" />
                        </Flex>
                      )}
                    </div>
                  ))}

                  {dayMeetings.map((item) => {
                    const start = dayjs(item.startTime);
                    const end = dayjs(item.endTime);
                    const top = (start.hour() + start.minute() / 60) * 60;
                    const duration = end.diff(start, "minute");
                    const height = (duration / 60) * 60;
                    const { color: bgColor } = getStatusTag(
                      item.approvalStatus as unknown as StepStatus,
                    );
                    const style = eventStyles.get(item.id);
                    const leftVal = style?.left || "0%";
                    const widthVal = style?.width || "100%";
                    const isMyMeeting =
                      item.createdByUser?.id === user?.user?.id;
                    const color =
                      isMyMeeting && item.approvalStatus === Status.APPROVED
                        ? "blue"
                        : bgColor;

                    return (
                      <Tooltip
                        title={
                          <Flex vertical gap={4} className="py-1">
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Mã cuộc họp:
                              </Text>{" "}
                              {item.id}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Tiêu đề:
                              </Text>{" "}
                              {item.title}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Phòng:
                              </Text>{" "}
                              {item.meetingRooms?.name || "Chưa chọn"}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Tầng:
                              </Text>{" "}
                              {item.meetingRooms?.floor || "Chưa chọn"}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Địa điểm:
                              </Text>{" "}
                              {item.meetingRooms?.location || "Chưa chọn"}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Sức chứa:
                              </Text>{" "}
                              {item.meetingRooms?.capacity || "Chưa chọn"}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Thời gian:
                              </Text>{" "}
                              {start.format("HH:mm")} - {end.format("HH:mm")}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Người tạo:
                              </Text>{" "}
                              {item.createdByUser?.id === user?.user?.id
                                ? "Tôi"
                                : item.createdByUser?.fullName || "Hệ thống"}
                            </Text>
                            <Text className="text-white text-xs">
                              <Text strong className="text-white text-xs">
                                Trạng thái:
                              </Text>{" "}
                              {
                                getStatusTag(
                                  item.approvalStatus as unknown as StepStatus,
                                ).label
                              }
                            </Text>
                          </Flex>
                        }
                        mouseEnterDelay={0.4}
                      >
                        <Tag
                          key={item.id}
                          color={color}
                          style={{
                            position: "absolute",
                            top,
                            height: Math.max(height, 24),
                            left: `calc(${leftVal} + 2px)`,
                            width: `calc(${widthVal} - 16px)`,
                            borderLeft: `4px solid ${color}`,
                          }}
                          className="rounded px-1.5 py-0.5 cursor-pointer shadow-sm z-10 hover:shadow-lg hover:z-50 transition-all"
                        >
                          {isMyMeeting && (
                            <Badge
                              count="Tôi"
                              color={
                                isMyMeeting &&
                                item.approvalStatus === Status.APPROVED
                                  ? "blue"
                                  : getStatusTag(
                                      item.approvalStatus as unknown as StepStatus,
                                    ).color
                              }
                              className="absolute -top-2 -right-2"
                            />
                          )}
                          <Flex justify="space-between" className="gap-1">
                            <Flex
                              align="center"
                              gap={4}
                              className="overflow-hidden"
                            >
                              {isMyMeeting && (
                                <UserOutlined
                                  className={`text-${color}-500 text-xs shrink-0 font-bold`}
                                />
                              )}
                              <Text
                                strong
                                className={`text-${color}-500 text-xs text-nowrap overflow-hidden text-ellipsis`}
                              >
                                {item.meetingRooms?.name} - {item.title}
                              </Text>
                            </Flex>

                            {isHrmView
                              ? item.approvalStatus === Status.PENDING && (
                                  <Flex align="center">
                                    <Popconfirm
                                      title="Duyệt lịch họp này?"
                                      onConfirm={(e) => {
                                        e?.stopPropagation();
                                        onReview?.(Status.APPROVED, item.id);
                                      }}
                                      okText="Có"
                                      cancelText="Không"
                                    >
                                      <Button
                                        type="primary"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        className="m-2"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </Popconfirm>
                                    <Popconfirm
                                      title="Từ chối lịch họp này?"
                                      onConfirm={(e) => {
                                        e?.stopPropagation();
                                        onReview?.(Status.REJECTED, item.id);
                                      }}
                                      okText="Có"
                                      cancelText="Không"
                                    >
                                      <Button
                                        type="primary"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </Popconfirm>
                                  </Flex>
                                )
                              : item.approvalStatus !== Status.APPROVED && (
                                  <Space size="small">
                                    {item.approvalStatus === Status.PENDING &&
                                      isMyMeeting && (
                                        <>
                                          <Button
                                            type="text"
                                            icon={
                                              <EditOutlined className="text-blue-500" />
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingMeeting(item);
                                              setIsEditModalOpen(true);
                                            }}
                                          />
                                          <Popconfirm
                                            title="Bạn có chắc chắn muốn xóa lịch họp này?"
                                            onConfirm={(e) => {
                                              e?.stopPropagation();
                                              handleDelete(item.id);
                                            }}
                                            okText="Có"
                                            cancelText="Không"
                                          >
                                            <Button
                                              type="text"
                                              danger
                                              icon={<DeleteOutlined />}
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            />
                                          </Popconfirm>
                                        </>
                                      )}
                                  </Space>
                                )}
                          </Flex>
                          <Flex justify="space-between" align="center">
                            {height >= 35 && (
                              <Text
                                style={{
                                  color: color,
                                  fontSize: 11,
                                  display: "block",
                                }}
                              >
                                {start.format("HH:mm")} - {end.format("HH:mm")}
                              </Text>
                            )}
                            {
                              getStatusTag(
                                item.approvalStatus as unknown as StepStatus,
                              ).label
                            }
                          </Flex>
                        </Tag>
                      </Tooltip>
                    );
                  })}

                  {isToday && (
                    <Flex
                      style={{
                        position: "absolute",
                        top: (dayjs().hour() + dayjs().minute() / 60) * 60,
                        left: 0,
                        right: 0,
                        borderTop: "2px solid #ea4335",
                        zIndex: 20,
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          left: -5,
                          top: -5,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: "#ea4335",
                        }}
                      />
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Flex>
        <ModalUpdateMeetingSchedule
          open={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setEditingMeeting(null);
          }}
          editingMeeting={editingMeeting}
        />
        <Flex>
          <Tag color="green">Đã duyệt</Tag>
          <Tag color="red">Đã bị từ chối</Tag>
          <Tag color="orange">Chờ duyệt</Tag>
        </Flex>
      </Flex>
    </div>
  );
};

export default WeeklyMeetingGrid;
